import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    document.body.classList.remove('cart-drawer-open');
  }

  #onDialogOpen = () => {
    document.body.classList.add('cart-drawer-open');
    this.#updateStickyState();
  };

  #onDialogClose = () => {
    document.body.classList.remove('cart-drawer-open');
  };

  #handleCartAdd = (event) => {
    // Callers that update the cart in place (e.g. the bulk order grid's "Add to box")
    // pass openDrawer: false so the bag stays closed.
    if (event.detail?.data?.openDrawer === false) return;
    // Open drawer on successful cart add (no error)
    if (!event.detail?.data?.didError) {
      // Small delay to allow cart content to update
      setTimeout(() => {
        if (this.isConnected) {
          this.showDialog();
          return;
        }

        // Morph can replace this element before the delay lands. Open the
        // live drawer instead of showModal() on the detached one.
        const live = document.querySelector('cart-drawer-component');
        if (live instanceof CartDrawerComponent) live.showDialog();
      }, 100);
    }
  };

  /**
   * Morphing an open <dialog> (add-to-cart section HTML, qty change) drops it
   * out of the browser top layer in Chrome while leaving [open] set. showDialog()
   * then no-ops because dialog.open is already true, and the sticky ATC bar
   * (position:fixed; z-index:999) paints over the bag. Close + showModal puts
   * it back in the top layer. Called from cart-items after every morph.
   */
  restoreTopLayer() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!(dialog instanceof HTMLDialogElement) || !dialog.isConnected || !dialog.open) return;

    document.body.classList.add('cart-drawer-open');
    if (dialog.matches(':modal')) return;

    dialog.close();
    dialog.showModal();
  }

  /**
   * If a morph left the drawer [open] but not :modal, close it first so the
   * inherited showDialog() does not return early on dialog.open.
   */
  showDialog() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (
      dialog instanceof HTMLDialogElement &&
      dialog.isConnected &&
      dialog.open &&
      !dialog.matches(':modal')
    ) {
      dialog.close();
    }
    super.showDialog();
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
